<template>
  <div class="admin-layout color-shift">
    <AdminSidebar @open-change-password-modal="isChangePasswordModalVisible = true" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Change Password Modal -->
      <ChangePasswordModal
        :is-visible="isChangePasswordModalVisible"
        @close="isChangePasswordModalVisible = false"
      />

      <!-- Single Delete Confirmation Modal -->
      <Modal
        ref="singleDeleteModal"
        title="Confirmation of Deletion"
        @confirm="handleSingleDeleteConfirm"
        @cancel="handleSingleDeleteCancel"
      >
        <p>Are you sure you want to delete this submission? This action cannot be undone.</p>
      </Modal>

      <!-- Bulk Delete Confirmation Modal -->
      <Modal
        ref="bulkDeleteModal"
        title="Bulk Deletion Confirmation"
        @confirm="handleBulkDeleteConfirm"
        @cancel="handleBulkDeleteCancel"
      >
        <p>Are you sure you want to delete {{ selectedSubmissions.length }} selected submissions? This action cannot be undone.</p>
      </Modal>

      <!-- Floating Bulk Delete Button -->
      <div
        v-if="selectedSubmissions.length > 0"
        class="floating-bulk-actions"
      >
        <button
          class="floating-delete-btn"
          :data-count="selectedSubmissions.length"
          @click="confirmBulkDelete"
        >
          <i class="fas fa-trash" />
          <span>Delete Selected ({{ selectedSubmissions.length }})</span>
        </button>
      </div>

      <div class="submissions-card">
        <div class="card-header">
          <p class="card-header-title">
            <i class="fas fa-table mr-3" />
            Submissions
          </p>
        </div>

        <!-- Enhanced Filters -->
        <div class="filters-section">
          <div class="filters-header">
            <h3 class="filters-title">
              <i class="fas fa-filter" />
              Filters & Search
            </h3>
            <button
              class="clear-filters-btn"
              @click="clearFilters"
            >
              <i class="fas fa-times" />
              Clear All
            </button>
          </div>
          
          <div class="filters-grid">
            <!-- Search Field -->
            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-search" />
                Search by Name/Email
              </label>
              <div class="input-wrapper">
                <input
                  v-model="filters.search"
                  type="text"
                  class="modern-input"
                  placeholder="Type to search..."
                  @input="debouncedSearch"
                >
                <i class="fas fa-search input-icon" />
              </div>
            </div>

            <!-- Major Filter -->
            <div class="filter-group">
              <label class="filter-label">
                <i class="fas fa-graduation-cap" />
                Filter by Major
              </label>
              <div class="select-wrapper">
                <select
                  v-model="filters.major"
                  class="modern-select"
                  @change="applyFilters"
                >
                  <option value="">
                    All Majors
                  </option>
                  <option
                    v-for="major in uniqueMajors"
                    :key="major"
                    :value="major"
                  >
                    {{ major }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Date Range -->
            <div class="filter-group date-range-group">
              <label class="filter-label">
                <i class="fas fa-calendar-alt" />
                Date Range
              </label>
              <div class="date-range-wrapper">
                <div class="date-input-wrapper">
                  <input
                    v-model="filters.dateFrom"
                    type="date"
                    class="modern-input date-input"
                    @change="applyFilters"
                  >
                </div>
                <span class="date-separator">to</span>
                <div class="date-input-wrapper">
                  <input
                    v-model="filters.dateTo"
                    type="date"
                    class="modern-input date-input"
                    @change="applyFilters"
                  >
                </div>
              </div>
            </div>

            <!-- Recommendations Filter -->
            <div class="filter-group recommendations-group">
              <label class="filter-label">
                <i class="fas fa-star" />
                Filter by Recommendations
              </label>
              <div class="checkbox-grid">
                <label
                  v-for="recommendation in recommendationOptions"
                  :key="recommendation"
                  class="checkbox-item"
                >
                  <input
                    v-model="filters.recommendations"
                    type="checkbox"
                    :value="recommendation"
                    class="modern-checkbox"
                    @change="applyFilters"
                  >
                  <span class="checkbox-label">{{ recommendation }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrapper">
          <table class="table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    class="modern-checkbox"
                    @change="selectAllSubmissions"
                  >
                </th>
                <th
                  v-for="column in columns"
                  :key="column.field"
                  class="sortable-header"
                >
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="submission in paginatedData"
                :key="submission.id"
                class="table-row"
              >
                <td>
                  <input
                    v-model="selectedSubmissions"
                    type="checkbox"
                    class="modern-checkbox"
                    :value="submission"
                  >
                </td>
                <td
                  v-for="column in columns"
                  :key="column.field"
                >
                  <template v-if="column.field === 'actions'">
                    <button
                      class="action-btn delete-btn"
                      @click="confirmDelete(submission.id)"
                    >
                      <i class="fas fa-trash-alt" />
                    </button>
                  </template>
                  <template v-else>
                    {{ getSubmissionValue(submission, column.field) }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Enhanced Pagination -->
        <div class="pagination-section">
          <div class="pagination-info">
            <div class="entries-info">
              <i class="fas fa-info-circle" />
              Showing <strong>{{ paginationStart }}</strong> to <strong>{{ paginationEnd }}</strong> of <strong>{{ totalItems }}</strong> entries
            </div>
            <div class="entries-selector">
              <label class="entries-label">
                <i class="fas fa-list" />
                Show entries:
              </label>
              <div class="select-wrapper small">
                <select
                  v-model="perPage"
                  class="modern-select small"
                  @change="onPerPageChange"
                >
                  <option value="10">
                    10
                  </option>
                  <option value="25">
                    25
                  </option>
                  <option value="50">
                    50
                  </option>
                  <option value="100">
                    100
                  </option>
                </select>
              </div>
            </div>
          </div>
          
          <div class="pagination-controls">
            <button
              class="pagination-btn"
              :class="{ disabled: currentPage === 1 }"
              :disabled="currentPage === 1"
              @click="onPageChange(1)"
            >
              <i class="fas fa-angle-double-left" />
            </button>
            
            <button
              class="pagination-btn"
              :class="{ disabled: currentPage === 1 }"
              :disabled="currentPage === 1"
              @click="onPageChange(currentPage - 1)"
            >
              <i class="fas fa-angle-left" />
              Previous
            </button>
            
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                class="page-btn"
                :class="{ active: page === currentPage }"
                @click="onPageChange(page)"
              >
                {{ page }}
              </button>
            </div>
            
            <button
              class="pagination-btn"
              :class="{ disabled: currentPage === lastPage }"
              :disabled="currentPage === lastPage"
              @click="onPageChange(currentPage + 1)"
            >
              Next
              <i class="fas fa-angle-right" />
            </button>
            
            <button
              class="pagination-btn"
              :class="{ disabled: currentPage === lastPage }"
              :disabled="currentPage === lastPage"
              @click="onPageChange(lastPage)"
            >
              <i class="fas fa-angle-double-right" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api.js';
import Modal from '@/components/Modal.vue';
import AdminSidebar from '@/components/AdminSidebar.vue';
import ChangePasswordModal from '@/components/ChangePasswordModal.vue';

export default {
  name: 'Dashboard',
  components: {
    Modal,
    AdminSidebar,
    ChangePasswordModal,
  },
  data() {
    return {
      submissions: [],
      selectedSubmissions: [],
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      lastPage: 1,
      loading: false,
      isMobile: false,
      showMobileFilters: false,
      filters: {
        search: '',
        major: '',
        dateFrom: null,
        dateTo: null,
        recommendations: []
      },
      recommendationOptions: [
        "Event",
        "Conference Programming",
        "Public Relations",
        "Marketing Communications",
        "Corporate Relations",
        "Operations",
        "Logistics",
        "Delegate Relations"
      ],
      columns: [
        { field: 'name', label: 'Name', sortable: true },
        { field: 'email', label: 'Email', sortable: true },
        { field: 'major', label: 'Major', sortable: true },
        { field: 'suggestions.0', label: 'Recommendation 1', sortable: true },
        { field: 'suggestions.1', label: 'Recommendation 2', sortable: true },
        { field: 'suggestions.2', label: 'Recommendation 3', sortable: true },
        { field: 'submitted_at', label: 'Submitted At', sortable: true },
        { field: 'actions', label: 'Actions' },
      ],
      searchTimeout: null,
      sortKey: '',
      sortOrder: 'asc',
      submissionToDelete: null,
      isChangePasswordModalVisible: false,
    };
  },
  computed: {
    paginationStart() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      const end = this.currentPage * this.perPage;
      return end > this.totalItems ? this.totalItems : end;
    },
    uniqueMajors() {
      return [
        "Management",
        "Accounting",
        "Economics",
        "Islamic Economics",
        "Islamic Business"
      ];
    },
    paginatedData() {
      let data = [...this.submissions];
      return data;
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.lastPage, this.currentPage + 2);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  watch: {
    'filters.search': {
      handler() {
        this.debouncedSearch();
      },
      deep: true
    },
    'filters.major': {
      handler() {
        this.applyFilters();
      }
    },
    'filters.dateFrom': {
      handler() {
        this.applyFilters();
      }
    },
    'filters.dateTo': {
      handler() {
        this.applyFilters();
      }
    },
    'filters.recommendations': {
      handler() {
        this.applyFilters();
      },
      deep: true
    }
  },
  async created() {
    await this.fetchSubmissions();
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
      if (!this.isMobile) {
        this.showMobileFilters = false;
      }
    },
    async fetchSubmissions() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage
        };
        if (this.filters.search) params.search = this.filters.search;
        if (this.filters.major) params.major = this.filters.major;
        if (this.filters.dateFrom) params.start_date = this.filters.dateFrom;
        if (this.filters.dateTo) params.end_date = this.filters.dateTo;
        if (this.filters.recommendations.length > 0) params.suggestions = this.filters.recommendations.join(',');

        const response = await api.getSubmissions(params);
        this.submissions = response.data.data.map((item) => ({
          ...item,
          email: item.email || '-',
          major: item.major || '-',
          submitted_at: this.formatDate(item.submitted_at),
        }));
        this.totalItems = response.data.total;
        this.lastPage = response.data.last_page;
      } catch (error) {
        console.error('Failed to fetch submissions:', error);
        if (error.response && [401, 403].includes(error.response.status)) {

        }
      } finally {
        this.loading = false;
      }
    },
    async applyFilters() {
      this.currentPage = 1;
      await this.fetchSubmissions();
    },
    async clearFilters() {
      this.filters = {
        search: '',
        major: '',
        dateFrom: null,
        dateTo: null,
        recommendations: []
      };
      this.applyFilters();
    },
    async onPageChange(page) {
      if (page < 1 || page > this.lastPage) return;
      this.currentPage = page;
      await this.fetchSubmissions();
    },
    async onPerPageChange() {
      this.currentPage = 1;
      await this.fetchSubmissions();
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('id-ID', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    async deleteSubmission(id) {
      try {
        await api.deleteSubmission(id);
        await this.fetchSubmissions();
        this.selectedSubmissions = [];
        this.$buefy.toast.open({
          message: 'Submission deleted successfully!',
          type: 'is-success',
          position: 'is-top-right',
        });
      } catch (error) {
        console.error('Failed to delete submission:', error);
      }
    },
    confirmDelete(id) {
      this.submissionToDelete = id;
      this.$refs.singleDeleteModal.open();
    },
    handleSingleDeleteConfirm() {
      if (this.submissionToDelete) {
        this.deleteSubmission(this.submissionToDelete);
        this.submissionToDelete = null;
      }
    },
    handleSingleDeleteCancel() {
      this.submissionToDelete = null;
    },
    confirmBulkDelete() {
      if (this.selectedSubmissions.length > 0) {
        this.$refs.bulkDeleteModal.open();
      }
    },
    handleBulkDeleteConfirm() {
      this.bulkDeleteSubmissions();
    },
    handleBulkDeleteCancel() {

    },
    async bulkDeleteSubmissions() {
      try {
        const ids = this.selectedSubmissions.map(submission => submission.id);
        await api.bulkDeleteSubmissions(ids);
        await this.fetchSubmissions();
        this.selectedSubmissions = [];
        this.$buefy.toast.open({
          message: `${ids.length} submissions deleted successfully!`,
          type: 'is-success',
          position: 'is-top-right',
        });
      } catch (error) {
        console.error('Failed to bulk delete submissions:', error);
      }
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 500);
    },
    getSubmissionValue(submission, field) {
      const fields = field.split('.');
      let value = submission;
      for (const f of fields) {
        if (value && typeof value === 'object' && f in value) {
          value = value[f];
        } else {
          return '-';
        }
      }
      return value;
    },
    selectAllSubmissions(event) {
      if (event.target.checked) {
        this.selectedSubmissions = [...this.submissions];
      } else {
        this.selectedSubmissions = [];
      }
    },
  }
};
</script>

<style scoped>
.admin-layout {
  display: flex;
  min-height: 100vh;
  animation: bg-color 5s linear infinite;
}

@keyframes bg-color {
  0% { background-color: #000942; }
  25% { background-color: #1e2b7e; }
  50% { background-color: #2c5acf; }
  75% { background-color: #002eb0; }
  100% { background-color: #000942; }
}

.main-content {
  flex-grow: 1;
  padding: 48px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 32px;
  margin: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 8px 25px rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;
}

.bulk-actions {
  margin-bottom: 20px;
}

/* Floating Bulk Actions */
.floating-bulk-actions {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

.floating-delete-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4), 
              0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.floating-delete-btn:hover {
  box-shadow: 0 12px 35px rgba(239, 68, 68, 0.5), 
              0 6px 18px rgba(0, 0, 0, 0.2);
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.floating-delete-btn:active {
  transform: translateY(0) translateX(-50%);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.floating-delete-btn i {
  font-size: 1.1rem;
}

.floating-delete-btn span {
  font-weight: 600;
  letter-spacing: 0.025em;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  border-bottom: 2px solid #e2e8f0;
}

.header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a202c;
}

.submissions-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
}

.card-header-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
}

.mr-3 {
  margin-right: 12px;
}

/* Enhanced Filters Styling */
.filters-section {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
}

.filters-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.clear-filters-btn {
  background: linear-gradient(135deg, #a0aec0 0%, #718096 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(160, 174, 192, 0.3);
}

.clear-filters-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(160, 174, 192, 0.4);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
}

.input-wrapper, .select-wrapper {
  position: relative;
}

.modern-input, .modern-select {
  width: 100%;
  padding: 12px 16px;
  padding-right: 40px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.875rem;
  background: white;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.modern-input:focus, .modern-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.input-icon, .select-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.date-range-group {
  grid-column: span 2;
}

.date-range-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-input-wrapper {
  position: relative;
  flex: 1;
}

.date-separator {
  color: #6b7280;
  font-weight: 500;
  padding: 0 8px;
}

.recommendations-group {
  grid-column: span 2;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 8px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
}

.checkbox-item:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.modern-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
}

.checkbox-label {
  font-size: 0.875rem;
  color: #374151;
}

/* Table Enhancements */
.table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.table th {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  position: relative;
}

.table th:first-child {
  border-top-left-radius: 12px;
}

.table th:last-child {
  border-top-right-radius: 12px;
}

.table td {
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.table-row:hover {
  background: #f9fafb;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.delete-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
}

.delete-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.4);
}

/* Enhanced Pagination */
.pagination-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e5e7eb;
}

.pagination-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.entries-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 0.875rem;
}

.entries-info strong {
  color: #374151;
  font-weight: 600;
}

.entries-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.entries-label {
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

.select-wrapper.small {
  min-width: 80px;
}

.modern-select.small {
  padding: 8px 12px;
  padding-right: 32px;
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.pagination-btn:hover:not(.disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.pagination-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
  margin: 0 12px;
}

.page-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.page-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.page-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-color: #3b82f6;
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .filters-grid {
    grid-template-columns: 1fr;
  }
  
  .date-range-group,
  .recommendations-group {
    grid-column: span 1;
  }
  
  .date-range-wrapper {
    flex-direction: column;
    gap: 8px;
  }
  
  .checkbox-grid {
    grid-template-columns: 1fr;
  }
  
  .pagination-info {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }
  
  .page-numbers {
    margin: 0;
  }

  /* Floating button responsive */
  .floating-bulk-actions {
    bottom: 16px;
    left: 16px;
    right: 16px;
    transform: none;
  }

  .floating-delete-btn {
    width: 100%;
    justify-content: center;
    padding: 14px 20px;
    font-size: 0.95rem;
  }

  .main-content {
    padding: 24px 16px;
    margin: 16px;
  }
}

@media (max-width: 480px) {
  .floating-delete-btn {
    padding: 12px 16px;
    font-size: 0.9rem;
  }

  .floating-delete-btn span {
    font-size: 0.85rem;
  }
}
</style>